import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Maximize2, Edit3, X } from 'lucide-react';

const DataGrid = ({
    data,
    headers,
    currentPage,
    rowsPerPage,
    previewMode,
    pendingChanges,
    manualChanges,
    editingCell,
    editValue,
    hoveredCell,
    onRowEdit,
    onCellClick,
    onCellHover,
    onCellHoverEnd,
    onEditValueChange,
    onSaveEdit,
    onCancelEdit,
    getOldValue,
    getCellValue,
    shouldUseTextarea
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ column: null, direction: null });

    // Helper to safely get printable value for cells
    const getSafeValue = (row, header) => {
        let val = getCellValue?.(row, header) || row[header];
        if (typeof val === 'object' && val !== null) {
            return val.content || JSON.stringify(val);
        }
        return val;
    };

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return data;

        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(row =>
            headers.some(header => {
                const value = getSafeValue(row, header);
                return value && String(value).toLowerCase().includes(lowerSearch);
            }) || (row._rowId && String(row._rowId).includes(lowerSearch))
        );
    }, [data, headers, searchTerm]);

    // Sort filtered data
    const sortedData = useMemo(() => {
        if (!sortConfig.column) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = getSafeValue(a, sortConfig.column);
            const bValue = getSafeValue(b, sortConfig.column);

            // Handle null/undefined
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Try numeric comparison first
            const aNum = parseFloat(aValue);
            const bNum = parseFloat(bValue);

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
            }

            // String comparison
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (sortConfig.direction === 'asc') {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
            }
        });
    }, [filteredData, sortConfig]);

    // Paginate sorted data
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentRows = sortedData.slice(startIndex, endIndex);

    // Filter headers to exclude _rowId
    const displayedHeaders = headers.filter(h => h !== '_rowId');

    // Handle sort
    const handleSort = (column) => {
        setSortConfig(prev => {
            if (prev.column === column) {
                // Cycle through: asc -> desc -> none
                if (prev.direction === 'asc') return { column, direction: 'desc' };
                if (prev.direction === 'desc') return { column: null, direction: null };
            }
            return { column, direction: 'asc' };
        });
    };

    // Get cell styling
    const getCellStyle = (row, header) => {
        let styles = '';

        if (!previewMode) {
            // Check for manual changes
            const manualKey = `${row._rowId}-${header}`;
            if (manualChanges?.[manualKey]) {
                styles += 'bg-yellow-50 border border-yellow-200 ring-1 ring-yellow-300 ';
            }
        } else {
            // Preview mode - check for pending changes
            const rowIdStr = row._rowId != null ? String(row._rowId) : null;

            // Check if row is deleted
            const isDeleted = rowIdStr && (pendingChanges?.deleted || []).some(id => String(id) === rowIdStr);

            // Check if row is added
            const isAdded = (pendingChanges?.added || []).some(addedRow => addedRow._rowId === row._rowId);

            // Check if cell has an update
            const updateChange = rowIdStr && (pendingChanges?.updated || []).find(u =>
                String(u.row_id) === rowIdStr && u.changes && u.changes[header]
            );

            if (isAdded) {
                styles += 'bg-blue-50 border border-blue-200 ring-2 ring-blue-300 ';
            } else if (isDeleted) {
                styles += 'bg-red-50 border border-red-200 line-through opacity-60 ';
            } else if (updateChange) {
                styles += 'bg-emerald-50 border border-emerald-200 ring-2 ring-emerald-300 ';
            }
        }

        return styles;
    };

    // Sort icon component
    const SortIcon = ({ column }) => {
        if (sortConfig.column !== column) {
            return <ArrowUpDown size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />;
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={14} className="text-blue-600" />
            : <ArrowDown size={14} className="text-blue-600" />;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Compact Search Bar */}
            <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-8 py-1.5 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all text-sm"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {searchTerm && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredData.length} of {data.length} rows
                    </span>
                )}
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto">
                {currentRows.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-full border-collapse bg-white">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-md">
                                    <th className="px-4 py-2.5 w-10 border-r border-blue-500 bg-blue-600 sticky left-0 z-20"></th>
                                    <th
                                        className="px-3 py-2.5 text-center font-semibold text-white text-sm border-r border-blue-500 w-16 min-w-[64px] group cursor-pointer hover:bg-blue-500 transition-colors"
                                        onClick={() => handleSort('_rowId')}
                                    >
                                        <div className="flex items-center justify-center gap-1">
                                            <span className="truncate">Row ID</span>
                                            <SortIcon column="_rowId" />
                                        </div>
                                    </th>
                                    {displayedHeaders.map(header => (
                                        <th
                                            key={header}
                                            className="px-4 py-2.5 text-left font-semibold text-white text-sm border-r border-blue-500 last:border-r-0 min-w-[150px] group cursor-pointer hover:bg-blue-500 transition-colors"
                                            onClick={() => handleSort(header)}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="truncate">{header}</span>
                                                <SortIcon column={header} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                <AnimatePresence mode="popLayout">
                                    {currentRows.map((row, index) => (
                                        <motion.tr
                                            key={row._rowId || index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2, delay: index * 0.01 }}
                                            className="hover:bg-blue-50/50 transition-all duration-150 group"
                                        >
                                            <td className="px-2 py-2.5 border-r border-gray-100 bg-white group-hover:bg-blue-50/50 sticky left-0 z-10 text-center">
                                                <button
                                                    onClick={() => onRowEdit?.(row)}
                                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-md transition-all hover:scale-110"
                                                    title="Open Row Editor"
                                                >
                                                    <Maximize2 size={14} />
                                                </button>
                                            </td>
                                            <td className={`px-3 py-2.5 border-r border-gray-100 text-sm font-mono text-gray-500 bg-gray-50/50 text-center font-semibold w-16 min-w-[64px] ${
                                                previewMode && row._rowId != null && (pendingChanges?.deleted || []).some(id => String(id) === String(row._rowId))
                                                    ? 'line-through opacity-60 bg-red-50'
                                                    : previewMode && (pendingChanges?.added || []).some(addedRow => addedRow._rowId === row._rowId)
                                                        ? 'bg-blue-50'
                                                        : ''
                                            }`}>
                                                {row._rowId}
                                            </td>
                                            {displayedHeaders.map(header => (
                                                <td
                                                    key={header}
                                                    className={`px-4 py-2.5 border-r border-gray-100 last:border-r-0 min-w-[150px] ${getCellStyle(row, header)} relative cursor-pointer transition-colors`}
                                                    onMouseEnter={() => {
                                                        const oldValue = getOldValue?.(row, header);
                                                        const manualKey = `${row._rowId}-${header}`;
                                                        const manualChange = manualChanges?.[manualKey];
                                                        if (oldValue !== null || manualChange) {
                                                            onCellHover?.({
                                                                rowId: row._rowId,
                                                                column: header,
                                                                oldValue: oldValue || (manualChange ? manualChange.oldValue : null),
                                                                isManual: !!manualChange && !oldValue
                                                            });
                                                        }
                                                    }}
                                                    onMouseLeave={() => onCellHoverEnd?.()}
                                                    onClick={() => {
                                                        if (!previewMode && editingCell?.rowId !== row._rowId && editingCell?.column !== header) {
                                                            onCellClick?.(row._rowId, header, getSafeValue(row, header));
                                                        }
                                                    }}
                                                    style={{
                                                        position: editingCell?.rowId === row._rowId && editingCell?.column === header ? 'relative' : 'relative',
                                                        zIndex: editingCell?.rowId === row._rowId && editingCell?.column === header ? 20 : 'auto'
                                                    }}
                                                >
                                                    {editingCell?.rowId === row._rowId && editingCell?.column === header ? (
                                                        <div className="relative">
                                                            {shouldUseTextarea?.(editValue) ? (
                                                                <textarea
                                                                    value={editValue}
                                                                    onChange={(e) => onEditValueChange?.(e.target.value)}
                                                                    onBlur={() => onSaveEdit?.()}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && e.ctrlKey) {
                                                                            onSaveEdit?.();
                                                                        } else if (e.key === 'Escape') {
                                                                            onCancelEdit?.();
                                                                        }
                                                                    }}
                                                                    autoFocus
                                                                    className="w-full p-2 border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[80px] resize-y"
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    value={editValue}
                                                                    onChange={(e) => onEditValueChange?.(e.target.value)}
                                                                    onBlur={() => onSaveEdit?.()}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            onSaveEdit?.();
                                                                        } else if (e.key === 'Escape') {
                                                                            onCancelEdit?.();
                                                                        }
                                                                    }}
                                                                    autoFocus
                                                                    className="w-full p-2 border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                                                />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-sm text-gray-700">
                                                            {getSafeValue(row, header)}
                                                            {!previewMode && (
                                                                <Edit3 size={12} className="inline-block ml-2 opacity-0 group-hover:opacity-50 transition-opacity" />
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Tooltip for old value */}
                                                    {hoveredCell &&
                                                        hoveredCell.rowId === row._rowId &&
                                                        hoveredCell.column === header && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 5 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-lg whitespace-nowrap pointer-events-none"
                                                            >
                                                                <div className="font-medium">
                                                                    {hoveredCell.isManual ? 'Original value:' : 'Previous value:'}
                                                                </div>
                                                                <div className="text-gray-300">{hoveredCell.oldValue}</div>
                                                                {/* Tooltip arrow */}
                                                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </motion.div>
                                                        )}
                                                </td>
                                            ))}
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center h-64 text-gray-400"
                    >
                        <Search size={48} className="mb-4 opacity-50" />
                        <p className="text-lg font-medium">No results found</p>
                        <p className="text-sm mt-2">Try adjusting your search term</p>
                        <button
                            onClick={() => setSearchTerm('')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        >
                            Clear Search
                        </button>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default DataGrid;
